import isEmpty from "lodash-es/isEmpty";
import React, { useCallback } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import { AccountControllerService, AuthControllerService } from "@/api";
import { ACTIVE, TRUE_STRING } from "@/configs/constants";
import services, { AccountTypes, IService } from "@/configs/services";
import {
  USER_LOCAL_STORAGE_KEY,
  ID_TOKEN_LOCAL_STORAGE_KEY,
  IS_DEMO_LOCAL_STORAGE_KEY,
  IS_AIMODE_LOCAL_STORAGE_KEY,
  TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
} from "@/configs/site";
import { getOverridesConfig } from "@/helpers/features";
import useMutation from "@/hooks/useMutation";
import { isTokenExpired } from "@/libs/store";
import { buildUserData, UserData } from "@/pages/login/useLogin";
import {
  Authority,
  AuthorityModel,
  ConfigParameters,
  ConfigParameter,
  IAccount,
  IUser,
  IUserInfo,
} from "@/types/api";

interface LoginAction {
  type: "LOGIN_SUCCESS";
  data: IUserInfo;
}

interface AccountAction {
  type: "SET_ACCOUNTS";
  data: IAccount[];
}

export interface NullAction {
  type: "DISABLED_MFA" | "SIGN_OUT_SUCCESS";
  data: null;
}

type LogginFailureAction = {
  type: "LOGIN_FAILURE";
};

type OAuthAccountAction = {
  type: "SET_OAUTH_ACCOUNT";
  data: string | null;
};

type Action =
  | LoginAction
  | NullAction
  | AccountAction
  | LogginFailureAction
  | OAuthAccountAction;

const UserStateContext = React.createContext({} as IUser);
const UserDispatchContext = React.createContext({} as React.Dispatch<Action>);

function saveUser(user: IUserInfo) {
  localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(user));
}

function userReducer(state: IUser, action: Action) {
  let newState;

  switch (action.type) {
    case "LOGIN_SUCCESS":
      return { ...state, user: action.data, isAuthenticated: true };
    case "SET_ACCOUNTS":
      newState = {
        ...state,
        user: { ...state.user, accounts: action.data || [] } as IUserInfo,
      };
      saveUser(newState.user);
      return newState;
    case "SET_OAUTH_ACCOUNT":
      newState = {
        ...state,
        user: {
          ...state.user,
          authAccountId: action.data,
        } as IUserInfo,
      };
      saveUser(newState.user);
      return newState;
    case "DISABLED_MFA":
      newState = {
        ...state,
        user: { ...state.user, mfaEnabled: false } as IUserInfo,
      };
      saveUser(newState.user);
      return newState;
    case "SIGN_OUT_SUCCESS":
      return { ...state, isAuthenticated: false };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * Lazy `useReducer` initializer — reads localStorage once on mount instead of
 * on every `UserProvider` render.
 */
function initUserState(): IUser {
  const storedUser = getUser();
  return {
    user: storedUser,
    // `getUser()` returns `{}` when nothing is stored, so check a real field.
    // The expiry check keeps this in sync with `hasValidSession()` in
    // @/Routes/loaders — without it an expired token leaves the context
    // authenticated while the loaders redirect to login, and App's
    // `if (isAuthenticated) return;` guard suppresses re-login.
    isAuthenticated: !!getToken() && !!storedUser.userId && !isTokenExpired(),
  };
}

function UserProvider({ children }: { children: React.ReactElement }) {
  const [state, dispatch] = React.useReducer(
    userReducer,
    undefined,
    initUserState,
  );

  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
}

function useUserState(): IUser {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUserState must be used within a UserProvider");
  }
  if (!context.user) {
    return {
      user: {} as IUserInfo,
      isAuthenticated: false,
    };
  }
  return context;
}

/** Stable empty list, so `useAccounts` does not allocate one per render. */
const EMPTY_ACCOUNTS: IAccount[] = [];

function useConfigParameters(): ConfigParameters {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useConfigParameters must be used within a UserProvider");
  }

  const configParams = context.user?.configParameters;

  // Memoized because callers put this object in dependency arrays. Returning a
  // fresh object every render invalidated their memos, which for anything
  // feeding `PageTitle` meant a `SET_TITLE` dispatch per render and an endless
  // re-render loop. Dev/test overrides are re-read whenever the user context
  // changes or the page reloads, which is when they are set anyway.
  return React.useMemo(
    () => ({ ...(configParams || {}), ...getOverridesConfig() }),
    [configParams],
  );
}

function useAccountConfigParameters(accountId: string): ConfigParameters {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error(
      "useAccountConfigParameters must be used within a UserProvider",
    );
  }

  const accounts = context.user?.accounts;

  // Memoized for the same reason as `useConfigParameters` above: this parsed a
  // fresh object out of JSON on every render, so any caller putting it in a
  // dependency array re-ran on every render.
  return React.useMemo(() => {
    const account =
      accounts?.find(item => item.id === accountId) || ({} as IAccount);
    return JSON.parse(account?.configParameters || "{}") as ConfigParameters;
  }, [accounts, accountId]);
}

function useAccounts(active?: boolean): IAccount[] {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useAccounts must be used within a UserProvider");
  }
  const accounts = context.user?.accounts;

  // Both branches allocated a new array per render — the empty fallback and the
  // filter alike — which invalidated callers' memos even though the underlying
  // list had not changed.
  return React.useMemo(() => {
    if (!accounts) return EMPTY_ACCOUNTS;
    return active
      ? accounts.filter(account => account.accountStatus === ACTIVE)
      : accounts;
  }, [accounts, active]);
}

/**
 * The tenant's data sources, or `undefined` while they have not loaded.
 *
 * `useAccounts` hands back one shared empty array for both "not loaded yet" and
 * "a tenant with none", so a count built on it reads zero while loading. This
 * keeps the two apart. It reads through the `UserContext` namespace so a spec
 * that stubs `useAccounts` (the only form Cypress can stub) still feeds it: a
 * stubbed list is a loaded list.
 */
function useLoadedAccounts(): IAccount[] | undefined {
  const accounts = UserContext.useAccounts();
  const context = React.useContext(UserStateContext);
  // A loaded user whose payload omits `accounts` is a tenant with none, not
  // one still loading.
  if (accounts !== EMPTY_ACCOUNTS) return accounts;
  return context?.user ? EMPTY_ACCOUNTS : undefined;
}

function useIsDemoTenant(): boolean {
  const storageValue =
    localStorage.getItem(IS_DEMO_LOCAL_STORAGE_KEY) === TRUE_STRING;
  const { user } = useUserState();
  return storageValue || !!user?.configParameters?.isDemo;
}

export type Authorities = keyof AuthorityModel;

export type AuthorityKey = `${Authorities}:${Authority}`;

export interface Props {
  hasAuthority: (name: AuthorityKey | AuthorityKey[]) => boolean;
  authority: AuthorityModel;
}
function useAuthority(): Props {
  const { user } = useUserState();
  const authority = React.useMemo(
    () => user?.authority || ({} as AuthorityModel),
    [user?.authority],
  );
  const configParams = React.useMemo(
    () => user?.configParameters || ({} as ConfigParameters),
    [user?.configParameters],
  );

  const hasAuthority = useCallback(
    (name: AuthorityKey | AuthorityKey[]): boolean => {
      const check = (single: AuthorityKey): boolean => {
        const [key, value] = single.split(":");
        if (configParams?.isDelegatedAdminEnabled) {
          if (!key) {
            // eslint-disable-next-line
            console.warn("Missing Authority Key");
            return false;
          }

          const result = authority[key as Authorities] || [];
          return result.includes("ALL") || result.includes(value as Authority);
        } else {
          return true;
        }
      };

      if (Array.isArray(name)) {
        return name.some(n => check(n as AuthorityKey));
      }
      return check(name);
    },
    [authority, configParams?.isDelegatedAdminEnabled],
  );

  return {
    hasAuthority,
    authority,
  };
}

function useUserDispatch(): React.Dispatch<Action> {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a UserProvider");
  }
  return context;
}

function useRefreshAccounts() {
  const userDispatch = useUserDispatch();
  return useCallback(async () => {
    return AccountControllerService.listAccountsUsingGet({
      includePolicyCount: true,
      pageNo: 1,
      pageSize: 999,
    }).then(response => {
      userDispatch({
        type: "SET_ACCOUNTS",
        data: response.items as IAccount[],
      });
    });
  }, [userDispatch]);
}

/**
 * Custom hook that ensures the user's account list is synchronized with a given set of account IDs.
 *
 * If any of the provided `accountIds` are not present in the current user's accounts,
 * this hook triggers a refresh of the account data.
 *
 * @param accountIds - An array of account IDs that should be present in the user's account list.
 *
 * @remarks
 * - Uses `useDeepCompareEffect` to watch for changes in the user's accounts.
 * - Calls `refreshAccounts` if any of the specified `accountIds` are missing.
 */
function useSyncAccounts(accountIds: string[]) {
  const refreshAccounts = useRefreshAccounts();
  const accounts = useAccounts();
  const userAccountIds = (accounts || []).map(account => account.id);

  useDeepCompareEffect(() => {
    if (isEmpty(accountIds)) {
      return;
    }

    const hasMissingAccount = accountIds.some(
      id => id && !userAccountIds.includes(id),
    );

    if (hasMissingAccount) {
      void refreshAccounts();
    }
  }, [accountIds, userAccountIds]);
}

/**
 * Custom hook to refresh the user's information by fetching the latest data
 * from the server and updating the user context.
 *
 * @returns {Object} An object containing:
 * - `refreshUser` (function): A mutation function to trigger the user refresh process.
 * - `isLoading` (boolean): A flag indicating whether the refresh operation is in progress.
 * - `error` (any): An error object if the refresh operation fails.
 *
 * The hook uses the `useMutation` hook to call the `AuthControllerService.userInfoUsingGet` API.
 * On successful response, it processes the user data using `buildUserData`, merges it with
 * existing accounts form current user state, and dispatches a `LOGIN_SUCCESS` action to update the user context.
 */
export function useRefreshUser() {
  const userDispatch = useUserDispatch();
  const SAMLToken = getToken();

  const {
    mutate: refreshUser,
    isLoading,
    error,
  } = useMutation(AuthControllerService.userInfoUsingGet, {
    onSuccess: async response => {
      const { user } = await buildUserData(
        response.data as UserData,
        SAMLToken,
      );
      const userState: IUserInfo = getUser();
      user.accounts = userState.accounts;
      userDispatch({ type: "LOGIN_SUCCESS", data: user }); // saving user's latest data and config with LOGIN_SUCCESS action type
    },
  });

  return {
    refreshUser,
    isLoading,
    error,
  };
}

/**
 * Check whether a given account supports a specific feature.
 *
 * If `accountId` is a string, resolves the account via `useAccounts(true)`.
 * Then finds the service config matching the account type and returns the
 * value for the provided `featureKey`.
 *
 * @param {IAccount | string} accountId - Account object or account id string.
 * @param {keyof IService} featureKey - Feature key to lookup on the service config.
 * @returns {any | undefined} The feature value from the matched service or `undefined` if not found.
 */
export const useCheckAccountFeature = (
  accountId: IAccount | string,
  featureKey: keyof IService,
): (typeof services)[number][keyof IService] | undefined => {
  const accounts = useAccounts(true);
  let account = accountId;
  if (typeof accountId === "string") {
    account = accounts.find(acc => acc.id === accountId)!;
  }

  const accountType = (account as IAccount)?.type;

  return services.find(service => service.value === accountType)?.[featureKey];
};

/**
 * Return accounts that support a given service feature.
 *
 * Looks up which service `value`s (account types) expose the provided
 * `featureKey` and filters the result of `useAccounts(true)` to include
 * only accounts whose `type` matches one of those service values.
 *
 * @param featureKey - Feature key to check on the service configuration.
 * @returns An array of `IAccount` instances that have the feature enabled.
 */
export const useAccountsWithFeature = (
  featureKey: keyof IService,
): IAccount[] => {
  const accounts = useAccounts(true);
  const servicesWithFeature = services
    .filter(service => Boolean(service[featureKey]))
    .map(service => service.value);
  return accounts.filter(account =>
    servicesWithFeature.includes(account.type as AccountTypes),
  );
};

function getUser(): IUserInfo {
  const userStr = localStorage.getItem(USER_LOCAL_STORAGE_KEY);
  return JSON.parse(userStr || "{}");
}

function getToken(): string {
  return localStorage.getItem(ID_TOKEN_LOCAL_STORAGE_KEY) || "";
}

/**
 * Binds an authority check to a user object the caller already holds.
 *
 * `hasAuthority` re-reads and re-parses the whole user blob out of
 * localStorage on every call, which is wasteful anywhere the check runs more
 * than once (route loaders resolve a whole navigation subtree). Callers with
 * the user in hand should bind once and reuse the closure.
 */
function authorityChecker(user: IUserInfo): (key: AuthorityKey) => boolean {
  const authority = user.authority || ({} as AuthorityModel);
  return authorityKey => {
    const [key, value] = authorityKey.split(":");
    const result = authority[key as Authorities] || [];
    return result.includes("ALL") || result.includes(value as Authority);
  };
}

function hasAuthority(authorityKey: AuthorityKey): boolean {
  return authorityChecker(getUser())(authorityKey);
}

/**
 * The tenant's config parameters with dev/test overrides (`?overrides=`)
 * merged on top - the same view `useConfigParameters` gives components. Non-hook
 * callers (route loaders) must read flags through this, or a flag the sidebar
 * sees as on will be treated as off when the route is resolved.
 */
function getConfigParameters(user: IUserInfo = getUser()): ConfigParameters {
  return {
    ...(user.configParameters || {}),
    ...getOverridesConfig(),
  } as ConfigParameters;
}

function hasConfigParameter(configParameter: ConfigParameter): boolean {
  return !!getConfigParameters()[configParameter];
}
// ###########################################################

function signOut(dispatch: React.Dispatch<Action>) {
  localStorage.removeItem(ID_TOKEN_LOCAL_STORAGE_KEY);
  localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
  localStorage.removeItem(IS_DEMO_LOCAL_STORAGE_KEY);
  localStorage.removeItem(IS_AIMODE_LOCAL_STORAGE_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
  dispatch({ type: "SIGN_OUT_SUCCESS" } as NullAction);
}

export {
  UserProvider,
  useUserState,
  useIsDemoTenant,
  useUserDispatch,
  useAuthority,
  useConfigParameters,
  useAccountConfigParameters,
  useAccounts,
  useRefreshAccounts,
  useSyncAccounts,
  signOut,
  saveUser,
};

// this done  as we need to stub the useAuthority hook in cypress tests
export const UserContext = {
  useAuthority,
  useUserState,
  useConfigParameters,
  useAccounts,
  useLoadedAccounts,
  useCheckAccountFeature,
  useAccountConfigParameters,
  hasAuthority,
  authorityChecker,
  getConfigParameters,
  hasConfigParameter,
  getToken,
  getUser,
  signOut,
};
